import React, { useEffect, useState, useRef } from 'react';
import { useApp } from '@/context/AppContext';
import { RefreshCw, X } from 'lucide-react';
import { getAutoUpdate } from '@/components/AutoUpdatePanel';

// A slim, always-visible bar that appears when a newer app build has been
// deployed and the service worker has fetched it in the background. Tapping the
// bar refreshes the page so staff instantly get the latest SHABZ CLOSET — no
// need to clear caches or reinstall. Shown on every screen (including Login) so
// users stuck on an old build can self-heal in one tap.
//
// If the owner has turned on "Update quietly in the background" for this device,
// the app will refresh ITSELF once it's idle or backgrounded — but only when
// it's safe: online, and with no offline sales still waiting to sync. That means
// it never interrupts an in-progress sale.
export function UpdateBanner() {
  const { online, pendingCount } = useApp();
  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [auto, setAuto] = useState(getAutoUpdate());

  // Detect a background service-worker update.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    let cancelled = false;
    const notify = () => { if (!cancelled) setReady(true); };

    const attach = (reg) => {
      if (!reg) return;
      if (reg.waiting && navigator.serviceWorker.controller) notify();
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener('statechange', () => {
          // 'installed' with an existing controller => this is an UPDATE, not a
          // first install, so it's safe to prompt for / apply a refresh.
          if (nw.state === 'installed' && navigator.serviceWorker.controller) notify();
        });
      });
      try { reg.update(); } catch (e) { /* ignore */ }
    };

    navigator.serviceWorker.getRegistration('/sw.js').then(attach).catch(() => {});
    navigator.serviceWorker.ready.then(attach).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Keep the auto-update preference in sync when it's toggled in Settings.
  useEffect(() => {
    const onChange = (e) => setAuto(!!e.detail);
    window.addEventListener('sc-auto-update-changed', onChange);
    return () => window.removeEventListener('sc-auto-update-changed', onChange);
  }, []);

  // Quiet auto-refresh when idle/backgrounded (opt-in, safe-guarded).
  const safeRef = useRef({ online, pendingCount });
  safeRef.current = { online, pendingCount };
  useEffect(() => {
    if (!ready || !auto) return;
    const IDLE_MS = 90 * 1000; // 1.5 min of no interaction counts as "idle"
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const events = ['pointerdown', 'keydown', 'pointermove', 'scroll', 'touchstart', 'wheel'];
    events.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));

    // Only refresh when it can't disrupt anything: online + nothing queued to sync.
    const canReload = () => safeRef.current.online && (safeRef.current.pendingCount || 0) === 0;
    const doReload = () => { try { window.location.reload(); } catch (e) { /* ignore */ } };

    const iv = setInterval(() => {
      if (canReload() && Date.now() - last > IDLE_MS) doReload();
    }, 5000);
    // Backgrounding the tab is the clearest "idle" signal — apply the update then.
    const onVis = () => { if (document.visibilityState === 'hidden' && canReload()) doReload(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, bump));
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [ready, auto]);

  if (!ready || dismissed) return null;

  const refresh = () => window.location.reload();
  const label = auto ? 'Update ready \u2014 refreshing when idle' : 'Update available \u2014 tap to refresh';

  return (
    <div
      className="fixed inset-x-0 top-0 z-[300]"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      role="status"
      aria-live="polite"
      data-testid="update-banner"
    >
      <div className="flex items-center gap-2 bg-primary px-4 py-2 text-primary-foreground shadow-md">
        <button
          type="button"
          onClick={refresh}
          data-testid="update-banner-refresh"
          className="flex flex-1 items-center justify-center gap-2 text-xs font-semibold transition-transform active:scale-[0.99]"
        >
          <RefreshCw className="h-3.5 w-3.5 shrink-0" />
          <span>{label}</span>
          <span className="ml-1 shrink-0 rounded-full bg-black/15 px-2 py-0.5 text-[10px] font-bold">Refresh</span>
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          data-testid="update-banner-dismiss"
          aria-label="Dismiss update notice"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-primary-foreground/80 transition-colors hover:bg-black/15 hover:text-primary-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
